import React, {useState, useEffect, useMemo} from 'react'
import {QUESTION_TYPE} from '../../utils/constants'
import WbOutlineButton from '../../components/common/WbOutlineButton'
import WbAlert from '../../components/common/WbAlert'
import renderSurveyQuestion from './renderSurveyQuestion'
import surveysApi from '../../services/api/surveys/surveysApi'
import {Grid, Stack, Box, CircularProgress, Button} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import {useParams} from 'react-router-dom'
import BorderLinearProgress from './BorderLinearProgress'
import LanguageTranslations from '../../utils/textConst'
import textToSpeechApi from '../../services/api/textToSpeach/textToSpeach'
import ContentScreenRender from '../../components/screens/surveyForms/items/ContentScreenRender'
import {entryKey, isContentScreen, toRouteItems} from '../../utils/surveyItems'
import {
  advance,
  canGoBack,
  currentIndex,
  goBack,
  isLastShown,
} from './runnerNavigation'
import {
  answerMapFromItems,
  answersOnRoute,
  firstShownIndex,
  makeRouteResolver,
  routeShownIndices,
} from './routeResolver'

export default function Questions({
  tab,
  questions: servedQuestions,
  setSurveyEnd,
  surveyData,
  language,
  ttsEnabled,
  voice,
}) {
  // Removed unused theme
  const {token} = useParams()

  // Answers are keyed by question id, so a question appearing on two slots cannot hold two
  // answers - the slots share one, and whichever is answered last discards the other. Nothing
  // stopped a survey being built that way (no unique index on the link table, no check on the
  // add paths), and survey 21677 in production was. Collapsing the duplicates is what makes the
  // rest of this component's assumption - one slot, one question, one answer - true again.
  const questions = useMemo(() => {
    if (!Array.isArray(servedQuestions)) return []
    const seen = new Set()
    // Skip-logic sections stay in the list so the route evaluator can read their
    // rules; they are never shown, because the resolver only ever lands on a
    // question or content screen.
    return servedQuestions.filter(q => {
      if (q == null || seen.has(entryKey(q))) return false
      seen.add(entryKey(q))
      return true
    })
  }, [servedQuestions])

  // Position is a stack of the item indices actually visited, so Back returns to
  // the last item shown (not index-1), and "next" comes from the route the
  // answers take rather than always being the adjacent slot.
  // The evaluator's view of the same list, index for index: rules name items by
  // their item id, while a question entry's own id is its bank question id.
  const routeItems = useMemo(() => toRouteItems(questions), [questions])
  const participantFacts = useMemo(() => surveyData?.participantFacts || {}, [surveyData])
  const [visited, setVisited] = useState(() => [firstShownIndex(routeItems, participantFacts)])
  const sel = currentIndex(visited)
  const [responses, setResponses] = useState([])
  const [audioUrl, setAudioUrl] = useState(null)
  const [audioReady, setAudioReady] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ttsLoading, setTtsLoading] = useState(false)

  // Routing: the server sends each condition's outcome as participantFacts, and
  // the shared evaluator turns the items + answers + facts into the shown path.
  // The visited-stack navigation asks resolveNext for the next shown index.
  const resolveNext = makeRouteResolver(participantFacts)
  const mapFor = answerArray => {
    const byId = {}
    ;(Array.isArray(answerArray) ? answerArray : []).forEach(r => {
      byId[r.questionId] = r
    })
    return answerMapFromItems(routeItems, byId, 'questionId')
  }
  const answerMap = mapFor(responses)
  const shownIndices = routeShownIndices(routeItems, answerMap, participantFacts)
  const shownPosition = shownIndices.indexOf(sel)

  // Initialize widget state from cookie
  useEffect(() => {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith('point_enabled='))

    console.log('Questions page cookie check:', {
      cookieValue: cookieValue?.split('=')[1],
      hasPoint: !!window.Point?.togglePoint,
    })

    // Only initialize the widget state once
    if (window.Point?.togglePoint) {
      // If cookie exists and is true, widget should be enabled
      if (cookieValue && cookieValue.split('=')[1] === 'true') {
        document.body.classList.add('point_enabled')
        console.log('Questions page cookie check: adding point_enabled class')
        document.cookie = `point_enabled=true; max-age=${90 * 24 * 60 * 60}; path=/`

        window.Point.togglePoint()
        console.log(
          'Questions page cookie check: setting cookie to true',
          window.Point.togglePoint()
        )
      } else {
        // If cookie is false or doesn't exist, widget should be disabled
        document.body.classList.remove('point_enabled')
        window.Point.togglePoint()
        // Set cookie to false if it doesn't exist
        if (!cookieValue) {
          document.cookie = `point_enabled=false; max-age=${90 * 24 * 60 * 60}; path=/`
        }
      }
    }
  }, [])

  // Back returns to the previous item actually shown (pops the visited stack).
  const goToPrevious = () => setVisited(goBack)

  const updateAnswers = value => {
    const current = questions[sel]
    if (!current) return

    // idx locates the answer; it is not a position in the question list. Reading questions[idx]
    // only lined up while the two arrays stayed one-to-one, and a duplicated question broke that.
    const resp = [...responses]
    const idx = resp.findIndex(r => r.questionId === value.questionId)
    if (idx === -1) {
      resp.push(value)
    } else {
      resp[idx] = value
    }
    // A new array, so React actually re-renders: the old code mutated `responses` in place and
    // handed the same reference back, which is why two slots of one question disagreed on screen.
    setResponses(resp)

    if (
      isLastShown(visited, routeItems, resolveNext, mapFor(resp)) ||
      current.questionType === QUESTION_TYPE.MULTIPLE_SELECT ||
      current.questionType === QUESTION_TYPE.RANKING ||
      current.questionType === QUESTION_TYPE.NUMBER_SLIDER ||
      current.questionType === QUESTION_TYPE.PERCENTAGE ||
      current.questionType === QUESTION_TYPE.COMMENTS
    ) {
      return
    }
    if (current.hasFollowup) {
      for (let fq of current.followupQuestions || []) {
        for (let i = 1; i <= 10; i++) {
          if (value[`value${i}`] === fq.followupValue) {
            return
          }
        }
      }
    }
    gotoNextQuestion(resp)
  }

  const submitResponses = () => {
    if (isSubmitting) return

    // Nothing on screen to validate means nothing to read a question type off. This used to throw
    // here - before the request, before any feedback - so Submit did nothing at all and said
    // nothing about why.
    const current = questions[sel]
    if (!current) {
      WbAlert({
        message: LanguageTranslations.getLanguage(language).errSelect,
        type: 'error',
      })
      return
    }

    if (
      current.questionType === QUESTION_TYPE.MULTIPLE_SELECT ||
      current.questionType === QUESTION_TYPE.YES_NO_MAYBE ||
      current.questionType === QUESTION_TYPE.YES_NO ||
      current.questionType === QUESTION_TYPE.MULTIPLE_CHOICE ||
      current.questionType === QUESTION_TYPE.LIKERT
    ) {
      let settings = current.questionSettings
      let operator = settings?.multiSelectOperator
      let val = settings?.multiSelectValue
      let res = responses?.find(x => x.questionId === current.id)
      if (res === undefined) {
        WbAlert({
          message: LanguageTranslations.getLanguage(language).errSelect,
          type: 'error',
        })
        return
      }
      let selectedCount = 0
      for (let i = 1; i <= 10; i++) {
        if (res[`value${i}`] !== null) {
          selectedCount++
        }
      }
      if (operator === 1 && selectedCount !== val) {
        WbAlert({
          message: LanguageTranslations.getLanguage(
            language
          ).errExactly.replace('{**}', val),
          type: 'error',
        })
        return
      }

      if (operator === 2 && selectedCount < val) {
        WbAlert({
          message: LanguageTranslations.getLanguage(
            language
          ).errAtleast.replace('{**}', val),
          type: 'error',
        })
        return
      }

      if (operator === 3 && selectedCount > val) {
        WbAlert({
          message: LanguageTranslations.getLanguage(language).errAtMost.replace(
            '{**}',
            val
          ),
          type: 'error',
        })
        return
      }
    }
    setIsSubmitting(true)
    surveysApi
      .submit(
        {
          participantToken: token,
          answers: answersOnRoute(routeItems, responses, participantFacts),
          audience: tab,
        },
        language
      )
      .then(() => {
        setSurveyEnd(true)
      })
      .catch(err => {
        setIsSubmitting(false)
        WbAlert({message: 'Error submitting response' + err, type: 'error'})
      })
  }

  const gotoNextQuestion = answersJustWritten => {
    const current = questions[sel]
    if (!current) return

    if (isContentScreen(current)) {
      setVisited(v => advance(v, routeItems, resolveNext, answerMap))
      return
    }

    // updateAnswers passes the answers it has just written, because its setState has not been
    // applied yet. Anything else (a DOM handler passing its event) falls back to state.
    const answers = Array.isArray(answersJustWritten) ? answersJustWritten : responses

    if (current.questionType === QUESTION_TYPE.MULTIPLE_SELECT) {
      let settings = current.questionSettings
      let operator = settings?.multiSelectOperator
      let val = settings?.multiSelectValue
      let res = answers?.find(x => x.questionId === current.id)
      if (res === undefined) {
        WbAlert({
          message: LanguageTranslations.getLanguage(language).errSelect,
          type: 'error',
        })
        return
      }

      let selectedCount = 0
      for (let i = 1; i <= 10; i++) {
        if (res[`value${i}`] !== null) {
          selectedCount++
        }
      }
      if (operator === 1 && selectedCount !== val) {
        WbAlert({
          message: LanguageTranslations.getLanguage(
            language
          ).errExactly.replace('{**}', val),
          type: 'error',
        })
        return
      }

      if (operator === 2 && selectedCount < val) {
        WbAlert({
          message: LanguageTranslations.getLanguage(
            language
          ).errAtleast.replace('{**}', val),
          type: 'error',
        })
        return
      }

      if (operator === 3 && selectedCount > val) {
        WbAlert({
          message: LanguageTranslations.getLanguage(language).errAtMost.replace(
            '{**}',
            val
          ),
          type: 'error',
        })
        return
      }
    }

    answers?.findIndex(x => x.questionId === current.id) > -1
      ? setVisited(v => advance(v, routeItems, resolveNext, mapFor(answers)))
      : WbAlert({
          message: LanguageTranslations.getLanguage(language).errSelect,
          type: 'error',
        })
    // Reset TTS state for next question
    setAudioReady(false)
    setAudioUrl(null)
    setTtsLoading(false)
  }

  // Helper to format question context for TTS, with question counter
  const getQuestionContext = (question, index, total) => {
    // Counter logic: First, Second, Third, ..., Last Question
    const ordinals = [
      'First',
      'Second',
      'Third',
      'Fourth',
      'Fifth',
      'Sixth',
      'Seventh',
      'Eighth',
      'Ninth',
      'Tenth',
      'Eleventh',
      'Twelfth',
      'Thirteenth',
      'Fourteenth',
      'Fifteenth',
      'Sixteenth',
      'Seventeenth',
      'Eighteenth',
      'Nineteenth',
      'Twentieth',
    ]
    let counterText = ''
    if (index === 0) {
      counterText = 'First Question'
    } else if (index === total - 1) {
      counterText = 'Last Question'
    } else if (index < ordinals.length) {
      counterText = `${ordinals[index]} Question`
    } else {
      counterText = `Question ${index + 1}`
    }

    // Pick question text based on tab and language
    let questionText = ''
    if (tab === 1) {
      questionText =
        language === 'en'
          ? question.staffText || ''
          : question.staffTextEnglish || ''
    } else if (tab === 2) {
      questionText =
        language === 'en'
          ? question.parentText || ''
          : question.parentTextEnglish || ''
    } else if (tab === 4) {
      questionText =
        language === 'en'
          ? question.pupilText || ''
          : question.pupilTextEnglish || ''
    } else {
      questionText = question.staffText || ''
    }
    let context = `${counterText}. ${questionText}`
    let optionsText = ''
    // Helper to pick option text based on tab and language
    const getOptionText = opt => {
      if (tab === 1) {
        return language === 'en'
          ? opt.staffText || ''
          : opt.staffTextEnglish || ''
      } else if (tab === 2) {
        return language === 'en'
          ? opt.parentText || ''
          : opt.parentTextEnglish || ''
      } else if (tab === 4) {
        return language === 'en'
          ? opt.pupilText || ''
          : opt.pupilTextEnglish || ''
      } else {
        return opt.staffText || ''
      }
    }
    switch (question.questionType) {
      case 1: // NUMBER_SLIDER
        context += '. Please select a value using the slider.'
        break
      case 2: // LIKERT
        if (
          Array.isArray(question.multiOptionResponseAttributes) &&
          question.multiOptionResponseAttributes.length > 0
        ) {
          optionsText = question.multiOptionResponseAttributes
            .map((opt, idx) => {
              const label = String.fromCharCode(65 + idx)
              const optionText = getOptionText(opt)
              return optionText ? `${label}. ${optionText}` : ''
            })
            .filter(Boolean)
            .join('... ')
          if (optionsText) context += `... Options: ${optionsText}.`
        }
        break
      case 3: // PERCENTAGE
        context += '. Please select a percentage value.'
        break
      case 4: // YES_NO
        if (
          Array.isArray(question.multiOptionResponseAttributes) &&
          question.multiOptionResponseAttributes.length > 0
        ) {
          optionsText = question.multiOptionResponseAttributes
            .map((opt, idx) => {
              const label = String.fromCharCode(65 + idx)
              const optionText = getOptionText(opt)
              return optionText ? `${label}. ${optionText}` : ''
            })
            .filter(Boolean)
            .join('... ')
          if (optionsText) context += `... Options: ${optionsText}.`
        }
        break
      case 5: // YES_NO_MAYBE
        if (
          Array.isArray(question.multiOptionResponseAttributes) &&
          question.multiOptionResponseAttributes.length > 0
        ) {
          optionsText = question.multiOptionResponseAttributes
            .map((opt, idx) => {
              const label = String.fromCharCode(65 + idx)
              const optionText = getOptionText(opt)
              return optionText ? `${label}. ${optionText}` : ''
            })
            .filter(Boolean)
            .join('... ')
          if (optionsText) context += `... Options: ${optionsText}.`
        }
        break
      case 6: // MULTIPLE_SELECT
        if (
          Array.isArray(question.multiOptionResponseAttributes) &&
          question.multiOptionResponseAttributes.length > 0
        ) {
          optionsText = question.multiOptionResponseAttributes
            .map((opt, idx) => {
              const label = String.fromCharCode(65 + idx)
              const optionText = getOptionText(opt)
              return optionText ? `${label}. ${optionText}` : ''
            })
            .filter(Boolean)
            .join('...')
          if (optionsText) context += `... Options: ${optionsText}.`
        }
        break
      case 7: // MULTIPLE_CHOICE
        if (
          Array.isArray(question.multiOptionResponseAttributes) &&
          question.multiOptionResponseAttributes.length > 0
        ) {
          optionsText = question.multiOptionResponseAttributes
            .map((opt, idx) => {
              const label = String.fromCharCode(65 + idx)
              const optionText = getOptionText(opt)
              return optionText ? `${label}. ${optionText}` : ''
            })
            .filter(Boolean)
            .join('... ')
          if (optionsText) context += `... Options: ${optionsText}.`
        }
        break
      case 8: // RANKING
        if (
          Array.isArray(question.multiOptionResponseAttributes) &&
          question.multiOptionResponseAttributes.length > 0
        ) {
          optionsText = question.multiOptionResponseAttributes
            .map(opt => getOptionText(opt))
            .filter(Boolean)
            .join(', ')
          if (optionsText)
            context += `. Please rank the following: ${optionsText}.`
        }
        break
      case 9: // COMMENTS
        context += '. Please provide your comments.'
        break
      default:
        break
    }
    // Fallback: if context is empty, use a default message
    if (!context || context.trim() === '') {
      context = 'Please answer the question.'
    }
    return context
  }

  // Play button for TTS audio (top right, next to theme icon, aligned and spaced)
  const topPlayButton = ttsEnabled && audioReady && (
    <Button
      onClick={() => {
        // Stop any currently playing audio before playing new one
        if (
          window.__welbeeCurrentAudio &&
          typeof window.__welbeeCurrentAudio.pause === 'function'
        ) {
          try {
            window.__welbeeCurrentAudio.pause()
            window.__welbeeCurrentAudio.currentTime = 0
          } catch (e) {}
          window.__welbeeCurrentAudio = null
        }
        if (audioUrl) {
          const audio = new window.Audio(audioUrl)
          window.__welbeeCurrentAudio = audio
          audio.play().catch(() => {})
        }
      }}
      sx={{
        minWidth: 0,
        padding: '6px',
        ml: 2,
        mr: 1,
        background: '#43a047',
        borderRadius: '50%',
        transition: 'background 0.2s',
        boxShadow: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          background: '#1b5e20',
        },
      }}
    >
      <PlayArrowIcon sx={{color: '#fff', fontSize: 28}} />
    </Button>
  )

  // Loader overlay for TTS (centered, green, with play icon, hides scrollbars)
  const loaderOverlay = ttsEnabled && ttsLoading && (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.4)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <style>{`
        body { overflow: hidden !important; }
        html { overflow: hidden !important; }
      `}</style>
      <Box sx={{position: 'relative', width: 80, height: 80}}>
        <CircularProgress
          size={80}
          thickness={5}
          sx={{color: '#43a047', position: 'absolute', top: 0, left: 0}}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 80,
            height: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlayArrowIcon
            sx={{
              fontSize: 48,
              color: '#fff',
              background: '#43a047',
              borderRadius: '50%',
              p: 1.25,
            }}
          />
        </Box>
      </Box>
    </Box>
  )

  const getPreview = templateQuestion =>
    renderSurveyQuestion(templateQuestion, {
      audience: tab,
      updateAnswers,
      language,
    })

  useEffect(() => {
    console.log('TTS useEffect called', {sel, ttsEnabled, language, voice})
    if (!ttsEnabled) {
      setAudioReady(false)
      setAudioUrl(null)
      setTtsLoading(false)
      return
    }
    setTtsLoading(true)
    setAudioReady(false)
    setAudioUrl(null)
    // Stop any currently playing audio before proceeding
    if (
      window.__welbeeCurrentAudio &&
      typeof window.__welbeeCurrentAudio.pause === 'function'
    ) {
      try {
        window.__welbeeCurrentAudio.pause()
        window.__welbeeCurrentAudio.currentTime = 0
      } catch (e) {}
      window.__welbeeCurrentAudio = null
    }
    const question = questions[sel]
    if (!question || isContentScreen(question)) {
      setTtsLoading(false)
      return
    }
    // "Question n of N" over the questions this respondent's route shows: sections and screens
    // are not questions, and a question the route skips is not one of theirs.
    const onlyQuestions = shownIndices.map(i => questions[i]).filter(q => q && !isContentScreen(q))
    const Question_Context = getQuestionContext(
      question,
      onlyQuestions.indexOf(question),
      onlyQuestions.length
    )
    console.log('Triggering TTS API call for question:', Question_Context)
    ;(async () => {
      try {
        const audioBlob = await textToSpeechApi.getQuestionTextToSpeech({
          text: Question_Context,
          languageCode: language,
          surveyId: surveyData?.surveyId,
          gender: voice,
          questionId: question.id,
          audience: String(tab),
        })
        const url = URL.createObjectURL(audioBlob)
        setAudioUrl(url)
        setAudioReady(true)
        // Auto-play audio as soon as it loads, and track it globally
        const audio = new window.Audio(url)
        window.__welbeeCurrentAudio = audio
        audio.play().catch(() => {})
      } catch (err) {
        setAudioReady(false)
        setAudioUrl(null)
        console.error('Question TTS audio fetch failed:', err)
      }
      setTtsLoading(false)
    })()
    // eslint-disable-next-line
  }, [sel, ttsEnabled, language, voice])

  return (
    <div className="survey-preview" id="survey-content">
      {/* Top right controls: theme icon and play button */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 75,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse', // Play button before theme icon
          gap: 2,
          paddingRight: 2,
        }}
      >
        {/* Theme icon should be last, play button before it */}
        {/* ...existing theme icon code (not shown here)... */}
        <Box sx={{mr: 2}}>{topPlayButton}</Box>
      </Box>
      {/* Hide scrollbars when loader is active */}
      {loaderOverlay}
      <div className="question-preview grow">
        <Grid container justifyContent="center" textAlign="center">
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <div className="preview">
              {questions?.map((q, i) => (
                <div
                  key={entryKey(q)}
                  className="question-slider"
                  style={{
                    transition: 'all 0.5s',
                    visibility: i === sel ? 'visible' : 'hidden',
                    opacity: i === sel ? 1 : 0,
                    transform:
                      i < sel
                        ? `translateX(${
                            i === sel
                              ? 0
                              : LanguageTranslations.getLanguage(language)
                                    .direction === 'rtl'
                                ? 100
                                : -100
                          }%)`
                        : `translateX(${
                            i === sel
                              ? 0
                              : LanguageTranslations.getLanguage(language)
                                    .direction === 'rtl'
                                ? -100
                                : 100
                          }%)`,
                  }}
                >
                  <div
                    className="question-box point_sym_hover"
                    style={{position: 'relative'}}
                  >
                    {/* TTS loader and play button should appear before options */}
                    {/* Removed duplicate loaderOverlay from question area */}
                    {/* Render question text first for TTS, then options */}
                    {isContentScreen(q) ? (
                      <ContentScreenRender content={q.content} />
                    ) : (
                      <>
                        {i === sel && (
                          <div style={{marginBottom: 16}}>
                            <strong>
                              {q.questionTextEn || q.questionText || ''}
                            </strong>
                          </div>
                        )}
                        {getPreview(q)}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="bottom-nav">
              <div className="question-nav">
                <Grid container>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Stack direction="row" justifyContent="space-between">
                      <div>
                        {!canGoBack(visited) ? (
                          ''
                        ) : (
                          <WbOutlineButton
                            onClick={goToPrevious}
                            CustomOutlineButtonText={
                              LanguageTranslations.getLanguage(language)
                                .previous
                            }
                          />
                        )}
                      </div>
                      <div>
                        {isLastShown(visited, routeItems, resolveNext, answerMap) ? (
                          <Button onClick={submitResponses} disabled={isSubmitting}>
                            {LanguageTranslations.getLanguage(language).submit}
                          </Button>
                        ) : (
                          <Button onClick={() => gotoNextQuestion()}>
                            {LanguageTranslations.getLanguage(language).next}
                          </Button>
                        )}
                      </div>
                    </Stack>
                  </Grid>
                </Grid>
              </div>
              <BorderLinearProgress
                variant="determinate"
                value={
                  shownIndices.length
                    ? (Math.max(shownPosition, 0) * 100) / shownIndices.length
                    : 0
                }
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </div>
  )
}
